// Child-side of the evisio2 ↔ inner-webtool postMessage bridge.
// Auto-bootstrapped by GlobalWidgetsLoader when `window.inEvisio2 === true`.
// Mirrors the pattern of MDSC_IFrameEventsHandler — arrow-bound listener, origin/source
// validation, idempotent destroy with isActive flag.

import topic from 'dojo/topic';
import lang from 'dojo/_base/lang';
import RequestManager from '../../../more/RequestManager';
import { FeatureHelpers } from "../../../more/Helpers";
import EvisioEmailEditor from '../evisioEmailEditor/evisioEmailEditor';
import "../evisioEmailEditor/evisioEmailEditor";
import {
    AppendEmailBodyPayload,
    BRIDGE_NAMESPACE_COMMAND,
    BRIDGE_TOPIC,
    CommandMeta,
    EvisioBridgeCommandName,
    EvisioBridgeErrorShape,
    EvisioBridgeMessage,
    GetEmailAttachmentPayload,
    HighlightCalendarOverviewPayload,
    OpenEmailEditorPayload,
    OpenEntityViewPayload,
    ReadyPayload,
    SetEmailDraftPayload,
    SetThemePayload,
    Theme,
} from "./EvisioBridgeTypes";

// Synchronous AMD require for runtime widget lazy-loading (see Helpers.provideFile).
// declared local because tsc otherwise can't find `require` in this module context.
declare var require: any; // workaround weil typescript dumm ist

const SINGLETON_KEY = "__evisio2BridgeClient";
const SET_THEME_MAX_ATTEMPTS = 50;

const PENDING_HIGHLIGHT_KEY = "evisio2_pendingHighlight";
const HIGHLIGHT_POLL_INTERVAL_MS = 200;
const HIGHLIGHT_POLL_MAX_MS = 6000;

interface PendingHighlight {
    requestId?: string;
    entityId: string;
    attempt: number;
}

class EvisioBridgeClient {
    public isActive: boolean = true;

    private _normalizedOrigin: string;
    private _readyEmitted: boolean = false;
    private _lastEmittedTheme: Theme | null = null;
    private _lastEmittedUrl: string | null = null;

    private _origPushState: (data: any, unused: string, url?: string | null) => void;
    private _origReplaceState: (data: any, unused: string, url?: string | null) => void;

    private _themeSubHandle: any = null;
    private _pendingSetThemePayload: SetThemePayload | null = null;
    private _pendingSetThemeAttempts: number = 0;
    private _pendingSetThemeRetryHandle: number | null = null;
    private _roleCheckSubHandle: any = null;
    private _highlightPollHandle: number | null = null;
    private _emailEditorOpenedSub: any = null;
    private _emailEditorClosedSub: any = null;

    public static instance(): EvisioBridgeClient {
        let existing = (window as any)[SINGLETON_KEY];
        if (existing && existing instanceof EvisioBridgeClient && existing.isActive){
            return existing;
        }
        let inst = new EvisioBridgeClient();
        (window as any)[SINGLETON_KEY] = inst;
        return inst;
    }

    private constructor(){
        try {
            this._normalizedOrigin = new URL(window.location.href).origin;
        } catch (e){
            this._normalizedOrigin = (window.location as any).origin || "";
        }

        window.addEventListener("message", this._onMessage);
        window.addEventListener("popstate", this._maybeEmitUrl);
        window.addEventListener("hashchange", this._maybeEmitUrl);

        this._patchHistory();

        this._themeSubHandle = topic.subscribe("Theme/Change", lang.hitch(this, this._onLocalThemeChange));

        // Editor lifecycle relays — the evisioEmailEditor wrapper publishes these locally
        // when the dialog opens / closes. We forward each as a postMessage event to the
        // parent so the parent-side wiring can register/unregister AI tools per session.
        this._emailEditorOpenedSub = topic.subscribe(BRIDGE_TOPIC.EmailEditorOpened, lang.hitch(this, this._onEditorOpenedLocal));
        this._emailEditorClosedSub = topic.subscribe(BRIDGE_TOPIC.EmailEditorClosed, lang.hitch(this, this._onEditorClosedLocal));

        // Defense-in-depth: even though the parent shell already gated by role before
        // loading this iframe, a user could hit `/<route>?inEvisio2=1` directly and
        // bypass that gate. Subscribe to the SessionInfo populate topic, and run a
        // sync check in case roles are already loaded at construction time.
        // The same subscription doubles as our trigger to emit ready once roles populate —
        // see _trySendReadyOnceRolesLoaded for why we wait.
        this._roleCheckSubHandle = topic.subscribe(
            "/Session/CheckForExistingSession",
            lang.hitch(this, this._onSessionPopulated)
        );
        this._onSessionPopulated();

        //RMTHIS-START — dev-only test hook proxy. Mirrors window.evisioTestTool from the
        //RMTHIS — parent (host) into this iframe so plain `evisioTestTool.openEntityView(...)`
        //RMTHIS — works in the iframe's DevTools console. Delete this block together with
        //RMTHIS — the host-side block in EvisioBridgeHost.ts (grep "RMTHIS").
        try {
            let parentTool = (window.top as any).evisioTestTool;
            if (parentTool){
                (window as any).evisioTestTool = parentTool;
                console.log("[evisioTestTool] proxy ready in iframe — call evisioTestTool.openEntityView(\"<id>\") directly");
            }
        } catch (e){ /* cross-origin top (e.g. embedded in Teams) — silently skip */ }
        //RMTHIS-END
    }

    // Topic handler for /Session/CheckForExistingSession. Runs role gate, then — if it
    // passed — triggers ready emission. Both depend on the same sessionInfo.Roles array
    // being populated, so they share a single trigger.
    private _onSessionPopulated(): void {
        if (!this.isActive) return;
        let roles = (window as any).sessionInfo?.sessionData?.Roles;
        if (!Array.isArray(roles)) return; // not loaded yet — wait for next topic fire
        if (roles.indexOf("Evisio2_GeneralAccess") === -1){
            console.warn("EvisioBridgeClient: user lacks Evisio2_GeneralAccess — disabling bridge");
            this.destroy();
            return;
        }
        // Role check passed — drop the subscription. Logout reloads the page, so we
        // don't need to re-check on subsequent session refreshes.
        if (this._roleCheckSubHandle){
            try { this._roleCheckSubHandle.remove(); } catch { /* ignore */ }
            this._roleCheckSubHandle = null;
        }
        // Ready emission is gated on roles so availableCommands is correct on first read.
        this._trySendReadyOnceRolesLoaded();

        // Resume a highlightCalendarOverview that triggered a navigation on the previous
        // page. Deferred until after the role gate so we never resume on a now-unauthorized session.
        this._resumePendingHighlightIfAny();
    }

    private _trySendReadyOnceRolesLoaded(): void {
        if (!this.isActive) return;
        if (this._readyEmitted) return;
        let roles = (window as any).sessionInfo?.sessionData?.Roles;
        if (!Array.isArray(roles)) return; // belt-and-suspenders — _onSessionPopulated already checked
        this._sendReady();
    }

    // Roles + features evaluated here so the host (and SDK by proxy) only sees commands
    // the current user can actually invoke. Predicates must mirror the gates inside the
    // matching _handleXxx methods — keep them in lockstep.
    private _computeAvailableCommands(): EvisioBridgeCommandName[] {
        let roles: string[] = (window as any).sessionInfo?.sessionData?.Roles || [];
        let has = (r: string) => roles.indexOf(r) !== -1;
        let out: EvisioBridgeCommandName[] = ["setTheme", "openEntityView"];
        let hasAgenda = FeatureHelpers.hasFeature("Planning", "EvBookingAgenda")
                     || FeatureHelpers.hasFeature("MonthColumnPlaner", "minimalModeAgendaView");
        if (hasAgenda && (has("Projects_Read") || has("PlanningView"))) out.push("highlightCalendarOverview");
        if (has("Email_Editor")){
            out.push("openEmailEditor");
            // Editor-scoped commands. Role-gated here at the available-commands layer so
            // they're known to the host/SDK shape; the wiring's open/close lifecycle then
            // controls whether the AI tools are actually registered with the Connection.
            // Per-call runtime check is in the EvisioEmailEditor wrapper (editorNotOpen).
            out.push("getEmailDraft");
            out.push("setEmailDraft");
            out.push("appendEmailBody");
            out.push("listEmailAttachments");
            out.push("getEmailAttachment");
        }
        return out;
    }

    // Pick the WidgetNavigationMap tile name to force-open on the /Planning/Navigation page
    // for the highlight resume path. Throws widgetNotFound if no calendar agenda feature is
    // enabled — the handler can fail fast instead of navigating to a page that can't possibly
    // mount the widget.
    private _pickAgendaTileName(): string {
        if (FeatureHelpers.hasFeature("Planning", "EvBookingAgenda")) return "EvBookingAgenda";
        if (FeatureHelpers.hasFeature("MonthColumnPlaner", "minimalModeAgendaView")) return "EvBookingMCP";
        throw { kind: "widgetNotFound", message: "no calendar agenda feature enabled for this customer" } as EvisioBridgeErrorShape;
    }

    private _patchHistory(): void {
        this._origPushState = window.history.pushState.bind(window.history);
        this._origReplaceState = window.history.replaceState.bind(window.history);

        let self = this;
        window.history.pushState = function (data: any, unused: string, url?: string | null): void {
            self._origPushState(data, unused, url);
            self._maybeEmitUrl();
        };
        window.history.replaceState = function (data: any, unused: string, url?: string | null): void {
            self._origReplaceState(data, unused, url);
            self._maybeEmitUrl();
        };
    }

    private _restoreHistory(): void {
        if (this._origPushState) window.history.pushState = this._origPushState;
        if (this._origReplaceState) window.history.replaceState = this._origReplaceState;
    }

    private _readStoredTheme(): Theme | null {
        let toggler: any = (window as any).themeToggler;
        if (!toggler || typeof toggler.getTheme !== "function") return null;
        try {
            let stored = toggler.getTheme(false);
            if (stored === "light" || stored === "dark" || stored === "auto") return stored;
        } catch (e){ /* ignore */ }
        return null;
    }

    private _sendReady(): void {
        if (!this.isActive) return;

        let theme: Theme = this._readStoredTheme() || "light";

        let payload: ReadyPayload = {
            languageCode: (window as any).currentLanguageCode || "",
            theme: theme,
            currentPath: window.location.pathname || "",
            currentSearch: window.location.search || "",
            currentHash: window.location.hash || "",
            availableCommands: this._computeAvailableCommands(),
        };

        let envelope: EvisioBridgeMessage = {
            kind: "evisio2:event",
            type: "ready",
            payload: payload,
        };

        this._lastEmittedTheme = theme;
        this._lastEmittedUrl = payload.currentPath + payload.currentSearch + payload.currentHash;

        try {
            window.parent.postMessage(envelope, this._normalizedOrigin);
        } catch (e){
            console.warn("EvisioBridgeClient: failed to send ready", e);
        }

        this._readyEmitted = true;
    }

    private _onMessage = (ev: MessageEvent): void => {
        if (!this.isActive) return;
        if (ev.source !== window.parent) return;
        if (ev.origin !== this._normalizedOrigin) return;

        let data: any = ev.data;
        if (typeof data === "string"){
            try { data = JSON.parse(data); } catch { return; }
        }
        if (!data || typeof data !== "object") return;

        // Silent filter — Teams/Syncfusion postMessages land here too. No console noise.
        if (data.kind !== BRIDGE_NAMESPACE_COMMAND) return;

        switch (data.type){
            case "setTheme":
                this._handleSetTheme(data.payload as SetThemePayload);
                break;
            case "openEntityView":
                this._runHandlerWithResult(data.meta, () => this._handleOpenEntityView(data.payload as OpenEntityViewPayload));
                break;
            case "highlightCalendarOverview":
                this._runHandlerWithResult(data.meta, () => this._handleHighlightCalendarOverview(data.payload as HighlightCalendarOverviewPayload, data.meta));
                break;
            case "openEmailEditor":
                this._runHandlerWithResult(data.meta, () => this._handleOpenEmailEditor(data.payload as OpenEmailEditorPayload));
                break;
            case "getEmailDraft":
                this._runHandlerWithResult(data.meta, () => this._handleGetEmailDraft());
                break;
            case "setEmailDraft":
                this._runHandlerWithResult(data.meta, () => this._handleSetEmailDraft(data.payload as SetEmailDraftPayload));
                break;
            case "appendEmailBody":
                this._runHandlerWithResult(data.meta, () => this._handleAppendEmailBody(data.payload as AppendEmailBodyPayload));
                break;
            case "listEmailAttachments":
                this._runHandlerWithResult(data.meta, () => this._handleListEmailAttachments());
                break;
            case "getEmailAttachment":
                this._runHandlerWithResult(data.meta, () => this._handleGetEmailAttachment(data.payload as GetEmailAttachmentPayload));
                break;
            default:
                console.warn("EvisioBridgeClient: unknown command type", data.type);
        }
    };

    private _onEditorOpenedLocal(): void {
        if (!this.isActive) return;
        let envelope: EvisioBridgeMessage = {
            kind: "evisio2:event",
            type: "emailEditorOpened",
            payload: {},
        };
        try { window.parent.postMessage(envelope, this._normalizedOrigin); }
        catch (e){ console.warn("EvisioBridgeClient: failed to send emailEditorOpened", e); }
    }

    private _onEditorClosedLocal(): void {
        if (!this.isActive) return;
        let envelope: EvisioBridgeMessage = {
            kind: "evisio2:event",
            type: "emailEditorClosed",
            payload: {},
        };
        try { window.parent.postMessage(envelope, this._normalizedOrigin); }
        catch (e){ console.warn("EvisioBridgeClient: failed to send emailEditorClosed", e); }
    }

    // Wraps an async handler: awaits it, then emits commandResult back to the host if
    // the command carried a requestId. setTheme stays untouched (no meta, no result).
    // If the handler intentionally suspends (e.g. highlightCalendarOverview navigates and
    // resumes on the next page), it returns the symbol below and we skip the result emit —
    // the resumed run will emit it after the reload.
    private async _runHandlerWithResult(meta: CommandMeta | undefined, fn: () => Promise<any>): Promise<void> {
        let result: any;
        let error: EvisioBridgeErrorShape | null = null;
        try {
            result = await fn();
        } catch (e: any) {
            if (e && typeof e === "object" && typeof e.kind === "string" && typeof e.message === "string"){
                error = e as EvisioBridgeErrorShape;
            } else {
                error = { kind: "handlerThrew", message: String((e && e.message) || e) };
            }
        }

        // Suspended handler (navigated, will resume) — don't emit a result here.
        if (!error && result === EvisioBridgeClient.SUSPENDED_RESULT) return;

        if (!meta || !meta.requestId) return; // fire-and-forget caller — no response needed

        this._emitCommandResult(meta.requestId, !error, result, error);
    }

    private _emitCommandResult(requestId: string, ok: boolean, result: any, error: EvisioBridgeErrorShape | null): void {
        let env: EvisioBridgeMessage = {
            kind: "evisio2:event",
            type: "commandResult",
            payload: {
                requestId: requestId,
                ok: ok,
                error: error || undefined,
                result: ok ? result : undefined,
            },
        };
        try {
            window.parent.postMessage(env, this._normalizedOrigin);
        } catch (e){
            console.warn("EvisioBridgeClient: failed to send commandResult", e);
        }
    }

    // Sentinel for handlers that navigate the iframe and resume after reload.
    private static readonly SUSPENDED_RESULT = { __evisio2Suspended: true };

    // PIT-IF: <hex>:<hex>, e.g. "0000038e:0000000c". Length capped at 16 per side
    // (DB PITs are 8 today; 16 leaves headroom and rejects pathological inputs).
    private static readonly PIT_ID_RE = /^[0-9a-fA-F]{1,16}:[0-9a-fA-F]{1,16}$/;
    // Class identifier: snake_case, starts with letter or underscore.
    private static readonly ENTITY_CLASS_RE = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;
    // Basic email shape — not RFC-strict, just enough to drop obvious garbage before
    // it lands in the recipient fields. Real validation happens at send time.
    private static readonly EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    private _guardPitId(id: any, where: string): string {
        if (typeof id !== "string" || !EvisioBridgeClient.PIT_ID_RE.test(id)){
            let preview = typeof id === "string" ? JSON.stringify(id.slice(0, 40)) : String(id);
            throw {
                kind: "invalidParams",
                message: where + ": " + preview + " is not a valid entity id. "
                       + "Expected format: two hex numbers joined by a colon, e.g. '0000038e:0000000c'. "
                       + "If you have a name or label instead of an id, use a search tool to resolve it to an id first — do NOT retry with the same value.",
            } as EvisioBridgeErrorShape;
        }
        return id;
    }

    private _guardEntityClass(cls: any, where: string): string {
        if (typeof cls !== "string" || !EvisioBridgeClient.ENTITY_CLASS_RE.test(cls)){
            let preview = typeof cls === "string" ? JSON.stringify(cls.slice(0, 40)) : String(cls);
            throw {
                kind: "invalidParams",
                message: where + ": " + preview + " is not a valid entity class. "
                       + "Expected a snake_case identifier like 'ev_booking', 'ev_project', or 'ev_contact'. "
                       + "If you don't know the class, omit this field — it will be resolved automatically from the id.",
            } as EvisioBridgeErrorShape;
        }
        return cls;
    }

    private _guardMaxLen(s: any, max: number, where: string): void {
        if (typeof s === "string" && s.length > max){
            throw {
                kind: "invalidParams",
                message: where + ": value is " + s.length + " characters, max allowed is " + max + ". "
                       + "Shorten the value before retrying.",
            } as EvisioBridgeErrorShape;
        }
    }

    private _filterEmails(list: string[] | undefined, perAddressMax: number): string[] | undefined {
        if (!Array.isArray(list)) return undefined;
        let out: string[] = [];
        for (let a of list){
            if (typeof a !== "string") continue;
            let t = a.trim();
            if (!t || t.length > perAddressMax) continue;
            if (!EvisioBridgeClient.EMAIL_RE.test(t)) continue;
            out.push(t);
        }
        return out.length ? out : undefined;
    }

    private _callBackend<T = any>(funcName: string, payload: any): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            let req = new RequestManager();
            req.callfunction(funcName, payload,
                (res: T) => resolve(res),
                (err: any) => reject({ kind: "backendError", message: String((err && err.Message) || err) } as EvisioBridgeErrorShape)
            );
        });
    }

    private _escapeHtml(s: string): string {
        return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    }

    private async _handleOpenEmailEditor(payload: OpenEmailEditorPayload): Promise<{ opened: true }> {
        // 1. Role gate — composing mail requires Email_Editor (same role the launch sites check).
        let roles: string[] = (window as any).sessionInfo?.sessionData?.Roles;
        if (!Array.isArray(roles) || roles.indexOf("Email_Editor") === -1){
            throw { kind: "roleDenied", message: "user lacks Email_Editor role" } as EvisioBridgeErrorShape;
        }

        if (!payload) payload = {} as OpenEmailEditorPayload;

        // Length caps — cheap DoS gate against an LLM stuffing payloads.
        // RFC 2822 caps subject lines at 998 chars; 256KB is generous for either body.
        this._guardMaxLen(payload.subject,       998,         "openEmailEditor.subject");
        this._guardMaxLen(payload.htmlBody,      256 * 1024,  "openEmailEditor.htmlBody");
        this._guardMaxLen(payload.plainTextBody, 256 * 1024,  "openEmailEditor.plainTextBody");

        // Recipient filter — drop addresses that don't match a basic email shape rather
        // than failing the whole open (recipients are user-reviewed before send anyway).
        // Per-address length cap = RFC 5321 max email length (320). Reject the whole list
        // up-front if it's an array of unreasonable size.
        for (let k of ["to", "cc", "bcc"] as const){
            let v = (payload as any)[k];
            if (Array.isArray(v) && v.length > 100){
                throw {
                    kind: "invalidParams",
                    message: "openEmailEditor." + k + ": recipient list has " + v.length + " entries, max allowed is 100. "
                           + "Trim the list before retrying.",
                } as EvisioBridgeErrorShape;
            }
        }
        let toClean  = this._filterEmails(payload.to,  320);
        let ccClean  = this._filterEmails(payload.cc,  320);
        let bccClean = this._filterEmails(payload.bcc, 320);

        // 2. Context — validate ONLY when both class and id are present. A partial pair is
        // ignored and the editor opens in the default history context. Format gates run
        // BEFORE the backend validation round-trip.
        let context: any = { isHistoryContext: true };
        if (payload.contextClass && payload.contextId){
            let contextClass = this._guardEntityClass(payload.contextClass, "openEmailEditor.contextClass");
            let contextId    = this._guardPitId(payload.contextId,          "openEmailEditor.contextId");
            let res = await this._callBackend<{ isValidClass: boolean; classWithIdExists: boolean }>(
                "_Evisio2__ValidateClassAndId",
                { class: contextClass, id: contextId }
            );
            if (!res || !res.isValidClass){
                throw { kind: "unknownEntity", message: "unknown class " + contextClass } as EvisioBridgeErrorShape;
            }
            if (!res.classWithIdExists){
                throw { kind: "unknownEntity", message: "no " + contextClass + " with id " + contextId } as EvisioBridgeErrorShape;
            }
            context = { isHistoryContext: true, contextEntity: { Classname: contextClass, ID: contextId } };
        }

        // 3. Body — htmlBody wins; otherwise plain text is escaped and newlines become <br>.
        let body: string | undefined;
        if (typeof payload.htmlBody === "string" && payload.htmlBody.length){
            body = payload.htmlBody;
        } else if (typeof payload.plainTextBody === "string" && payload.plainTextBody.length){
            body = this._escapeHtml(payload.plainTextBody).replace(/\r\n|\r|\n/g, "<br>");
        }

        // 4. Delegate the actual open + singleton enforcement + lifecycle pub to the wrapper.
        // Wrapper rejects with editorAlreadyOpen if a session is in progress. Pass sanitized
        // recipient lists; everything else (subject, context, body) stays on the payload.
        let cleanPayload: OpenEmailEditorPayload = {
            subject:       payload.subject,
            htmlBody:      payload.htmlBody,
            plainTextBody: payload.plainTextBody,
            to:            toClean,
            cc:            ccClean,
            bcc:           bccClean,
            contextClass:  payload.contextClass,
            contextId:     payload.contextId,
        };
        return await EvisioEmailEditor.instance().open(cleanPayload, context, body);
    }

    private async _handleGetEmailDraft(): Promise<any> {
        return EvisioEmailEditor.instance().getDraft();
    }

    private async _handleSetEmailDraft(payload: SetEmailDraftPayload): Promise<any> {
        return EvisioEmailEditor.instance().setDraft(payload);
    }

    private async _handleAppendEmailBody(payload: AppendEmailBodyPayload): Promise<any> {
        return EvisioEmailEditor.instance().appendBody(payload);
    }

    private async _handleListEmailAttachments(): Promise<any> {
        return EvisioEmailEditor.instance().listAttachments();
    }

    private async _handleGetEmailAttachment(payload: GetEmailAttachmentPayload): Promise<any> {
        return await EvisioEmailEditor.instance().getAttachmentBytes(payload);
    }

    private async _handleOpenEntityView(payload: OpenEntityViewPayload): Promise<void> {
        if (!payload){
            throw { kind: "invalidParams", message: "openEntityView: missing payload" } as EvisioBridgeErrorShape;
        }

        // Pre-flight format gates — fail fast before any backend round-trip so the LLM
        // sees a precise hint (and we don't waste a request when it passes a name like
        // "Musikverein Wien" instead of a PIT id).
        let entityId = this._guardPitId(payload.entityId, "openEntityView");

        let entityClass = payload.entityClass || "";
        if (entityClass){
            entityClass = this._guardEntityClass(entityClass, "openEntityView");
        }

        // Defensive shape-check on the pass-through bag. Not exposed in the current AI
        // tool schema, but any bridge caller could land arbitrary `any` here.
        if (payload.additionalParams != null){
            if (typeof payload.additionalParams !== "object" || Array.isArray(payload.additionalParams)){
                throw { kind: "invalidParams", message: "openEntityView: additionalParams must be a plain object or null" } as EvisioBridgeErrorShape;
            }
        }

        if (!entityClass){
            let res = await this._callBackend<{ searchSuccessfull: boolean; classname?: string }>(
                "Evisio2__Helpers_TrySearchClassnameByEntityId",
                { entityID: entityId, scopeToEntityView: true }
            );
            if (!res || !res.searchSuccessfull || !res.classname){
                throw { kind: "unknownEntity", message: "no class found for id " + entityId } as EvisioBridgeErrorShape;
            }
            entityClass = res.classname;
        }

        topic.publish("/Entity/OpenView", {
            entityId: entityId,
            entityClass: entityClass,
            label: payload.label != null ? payload.label : "",
            additionalParams: payload.additionalParams != null ? payload.additionalParams : null,
        });
    }

    private async _handleHighlightCalendarOverview(payload: HighlightCalendarOverviewPayload, meta: CommandMeta | undefined): Promise<any> {
        if (!payload){
            throw { kind: "invalidParams", message: "highlightCalendarOverview: missing payload" } as EvisioBridgeErrorShape;
        }

        // Pre-flight PIT-id format gate — distinct kind from `notABooking`, which is
        // reserved for "id parses but DB says it's not an ev_booking".
        let entityId = this._guardPitId(payload.entityId, "highlightCalendarOverview");

        // 1. Up-front fail-fast: no calendar feature enabled → no widget can ever mount.
        // Throwing here skips the backend round-trip and surfaces a precise reason to the LLM
        // instead of a 30s handlerThrew timeout further down the resume path.
        this._pickAgendaTileName();

        // 2. Validate via backend.
        let isBookingRes = await this._callBackend<{ isEvBooking: boolean }>(
            "Evisio2__Helpers_IsEvBookingId",
            { entityID: entityId }
        );
        if (!isBookingRes || !isBookingRes.isEvBooking){
            throw { kind: "notABooking", message: "id " + entityId + " is not an ev_booking" } as EvisioBridgeErrorShape;
        }

        // 3. Role gate. Calendar widgets require Projects_Read or PlanningView; bridge alone
        // (Evisio2_GeneralAccess) isn't enough to actually land on the widget.
        let roles: string[] = (window as any).sessionInfo?.sessionData?.Roles;
        if (!Array.isArray(roles) || (roles.indexOf("Projects_Read") === -1 && roles.indexOf("PlanningView") === -1)){
            throw { kind: "roleDenied", message: "highlight requires Projects_Read or PlanningView" } as EvisioBridgeErrorShape;
        }

        // 4. Find a visible agenda widget on the current page.
        let widget = this._findVisibleAgendaWidget();

        if (widget){
            // 5. Visible — apply highlight directly.
            return await this._applyHighlightToWidget(widget, entityId);
        }

        // 6. Not visible — stash and navigate to the Planning navigation with ?nav=<tile>
        // so WidgetNavigationMap.setup() force-opens the calendar tile on the new page
        // (independent of the customer's Planning.defaultOpen config). The new page's
        // bridge client picks up the pending highlight in _resumePendingHighlightIfAny;
        // this handler suspends, and the resumed run emits commandResult.
        let tileName = this._pickAgendaTileName();
        let pending: PendingHighlight = {
            requestId: meta && meta.requestId,
            entityId: entityId,
            attempt: 1,
        };
        try { sessionStorage.setItem(PENDING_HIGHLIGHT_KEY, JSON.stringify(pending)); } catch { /* storage full / disabled — best effort */ }

        let appRoot: string = (window as any).appRoot || "/";
        window.location.replace(appRoot + "Planning/Navigation?nav=" + encodeURIComponent(tileName));
        return EvisioBridgeClient.SUSPENDED_RESULT;
    }

    private _findVisibleAgendaWidget(): any | null {
        let dijits: { [k: string]: any } = (window as any)["dijits"] || {};
        let mcpFeature = FeatureHelpers.hasFeature("MonthColumnPlaner", "minimalModeAgendaView");
        for (let key in dijits){
            let w = dijits[key];
            if (!w || typeof w.evisioHook_jumpToAppointment !== "function") continue;
            // EvBookingAgenda hook's first line is this.agendaView.datePicker.setValue(...).
            // Gating on that path prevents a pre-startup widget from throwing under us.
            if (key.indexOf("EvBookingAgenda") !== -1 && w.agendaView && w.agendaView.datePicker) return w;
            // MonthColumnPlanMinimalMode hook touches this.mainDatePicker + this.mainWidget.monthColumnPlan.
            if (mcpFeature && key.indexOf("MonthColumnPlanMinimalMode") !== -1 && w.mainDatePicker && w.mainWidget) return w;
        }
        return null;
    }

    private async _applyHighlightToWidget(widget: any, bookingId: string): Promise<void> {
        let appointmentData = await this._callBackend<any>(
            "Webtool_API_EvisioAssistant_AppointmentHelper",
            { bookingID: bookingId }
        );

        // Tab-switch if the widget lives inside a tab (mirrors legacy executeAction__highlightAgenda).
        if (widget && widget.isEmbeddedInTab === true && typeof widget.requestTabSwitchGlobal === "function"){
            for (let tn of ["evbAgenda", "agendaMCP"]){
                try { widget.requestTabSwitchGlobal(tn); } catch { /* ignore */ }
            }
        }

        if (typeof widget.evisioHook_jumpToAppointment !== "function"){
            throw { kind: "widgetNotFound", message: "widget has no evisioHook_jumpToAppointment" } as EvisioBridgeErrorShape;
        }
        widget.evisioHook_jumpToAppointment(appointmentData);
    }

    private _resumePendingHighlightIfAny(): void {
        let raw: string | null;
        try { raw = sessionStorage.getItem(PENDING_HIGHLIGHT_KEY); } catch { raw = null; }
        if (!raw) return;
        try { sessionStorage.removeItem(PENDING_HIGHLIGHT_KEY); } catch { /* ignore */ }

        let pending: PendingHighlight;
        try { pending = JSON.parse(raw); } catch { return; }
        if (!pending || !pending.entityId) return;

        let requestId = pending.requestId;
        let entityId = pending.entityId;
        let attempts = pending.attempt || 1;

        let startedAt = Date.now();
        let poll = () => {
            if (!this.isActive) return;

            let widget = this._findVisibleAgendaWidget();
            if (widget){
                this._highlightPollHandle = null;
                this._applyHighlightToWidget(widget, entityId).then(
                    () => { if (requestId) this._emitCommandResult(requestId, true, undefined, null); },
                    (err: EvisioBridgeErrorShape) => { if (requestId) this._emitCommandResult(requestId, false, undefined, err); }
                );
                return;
            }

            if (Date.now() - startedAt >= HIGHLIGHT_POLL_MAX_MS){
                // Give up. If attempts > 1, we already navigated once — don't loop.
                this._highlightPollHandle = null;
                if (requestId){
                    this._emitCommandResult(requestId, false, undefined,
                        { kind: "widgetNotFound", message: "no agenda widget after " + (HIGHLIGHT_POLL_MAX_MS / 1000) + "s on attempt " + attempts });
                }
                return;
            }

            this._highlightPollHandle = window.setTimeout(poll, HIGHLIGHT_POLL_INTERVAL_MS);
        };
        this._highlightPollHandle = window.setTimeout(poll, HIGHLIGHT_POLL_INTERVAL_MS);
    }

    private _handleSetTheme(payload: SetThemePayload): void {
        if (!payload || !payload.theme) return;
        if (payload.theme !== "light" && payload.theme !== "dark" && payload.theme !== "auto") return;

        let toggler: any = (window as any).themeToggler;
        if (!toggler || typeof toggler.setTheme !== "function"){
            // ThemeToggler not yet booted — defer with the latest payload wins, bounded retries.
            this._pendingSetThemePayload = payload;

            if (this._pendingSetThemeRetryHandle != null) return; // retry already scheduled

            if (this._pendingSetThemeAttempts >= SET_THEME_MAX_ATTEMPTS){
                console.warn("EvisioBridgeClient: setTheme dropped — themeToggler never booted");
                this._pendingSetThemePayload = null;
                this._pendingSetThemeAttempts = 0;
                return;
            }
            this._pendingSetThemeAttempts++;
            this._pendingSetThemeRetryHandle = window.setTimeout(() => {
                this._pendingSetThemeRetryHandle = null;
                let latest = this._pendingSetThemePayload;
                if (!latest) return;
                this._pendingSetThemePayload = null;
                this._handleSetTheme(latest);
            }, 0);
            return;
        }

        // Toggler is available — apply. Reset retry bookkeeping.
        this._pendingSetThemePayload = null;
        this._pendingSetThemeAttempts = 0;

        let current = this._readStoredTheme();
        if (current === payload.theme) return; // loop break

        this._lastEmittedTheme = payload.theme;
        toggler.setTheme(payload.theme);
    }

    private _onLocalThemeChange(_theme: any): void {
        if (!this.isActive) return;
        if (!this._readyEmitted) return;

        let current = this._readStoredTheme();
        if (!current) return;
        if (current === this._lastEmittedTheme) return; // loop break — already emitted or just received

        this._lastEmittedTheme = current;

        let envelope: EvisioBridgeMessage = {
            kind: "evisio2:event",
            type: "themeChanged",
            payload: { theme: current },
        };
        try {
            window.parent.postMessage(envelope, this._normalizedOrigin);
        } catch (e){
            console.warn("EvisioBridgeClient: failed to send themeChanged", e);
        }
    }

    private _maybeEmitUrl = (): void => {
        if (!this.isActive) return;
        if (!this._readyEmitted) return; // initial state already in ready payload

        let composed = window.location.pathname + window.location.search + window.location.hash;
        if (composed === this._lastEmittedUrl) return; // dedupe — kills dialog pushState noise

        this._lastEmittedUrl = composed;

        let envelope: EvisioBridgeMessage = {
            kind: "evisio2:event",
            type: "urlChanged",
            payload: {
                path: window.location.pathname || "",
                search: window.location.search || "",
                hash: window.location.hash || "",
            },
        };
        try {
            window.parent.postMessage(envelope, this._normalizedOrigin);
        } catch (e){
            console.warn("EvisioBridgeClient: failed to send urlChanged", e);
        }
    };

    public destroy(): void {
        if (!this.isActive) return;
        this.isActive = false;

        window.removeEventListener("message", this._onMessage);
        window.removeEventListener("popstate", this._maybeEmitUrl);
        window.removeEventListener("hashchange", this._maybeEmitUrl);

        this._restoreHistory();

        if (this._themeSubHandle){
            try { this._themeSubHandle.remove(); } catch { /* ignore */ }
            this._themeSubHandle = null;
        }

        if (this._roleCheckSubHandle){
            try { this._roleCheckSubHandle.remove(); } catch { /* ignore */ }
            this._roleCheckSubHandle = null;
        }

        if (this._emailEditorOpenedSub){
            try { this._emailEditorOpenedSub.remove(); } catch { /* ignore */ }
            this._emailEditorOpenedSub = null;
        }
        if (this._emailEditorClosedSub){
            try { this._emailEditorClosedSub.remove(); } catch { /* ignore */ }
            this._emailEditorClosedSub = null;
        }

        if (this._pendingSetThemeRetryHandle != null){
            clearTimeout(this._pendingSetThemeRetryHandle);
            this._pendingSetThemeRetryHandle = null;
        }
        this._pendingSetThemePayload = null;
        this._pendingSetThemeAttempts = 0;

        if (this._highlightPollHandle != null){
            clearTimeout(this._highlightPollHandle);
            this._highlightPollHandle = null;
        }

        try { delete (window as any)[SINGLETON_KEY]; } catch { (window as any)[SINGLETON_KEY] = undefined; }
    }
}

export default EvisioBridgeClient;
